import { useMemo } from 'react';
import { useAssistantTool } from '@assistant-ui/react';
import { useHistory } from 'react-router-dom';
import { searchContacts } from '../../../utils/RocksetQueries/SearchQueries';
import { P } from '../../../utils/permissions';
import {
  DASHBOARD_ASSISTANT_RUNTIME_MODE,
  MAX_SEARCH_RESULTS_PER_CATEGORY,
} from '../constants';
import { stringToolParameters } from '../lib/assistantToolSchemas';
import { hasPermission, restrictedResult } from '../lib/permissions';
import {
  dedupeBy,
  getContactMatchScore,
  getMerchantNames,
  limitItems,
} from '../lib/helpers';
import {
  createFrontendNavigationToolMessage,
  createFrontendToolActionMessage,
  createFrontendToolErrorMessage,
} from '../lib/toolActionMessages';
import { serializeSearchContact } from '../lib/serializers';
import { buildSearchResult, createCompactSearchToolResult } from '../lib/searchResultBuilders';

export function useFindAndOpenContactAction({
  activeSessionId,
  dispatchActionMessage,
  organizationName,
}) {
  const history = useHistory();

  const parameters = useMemo(
    () =>
      stringToolParameters({
        name: 'query',
        description: 'Contact name or email to find and open.',
      }),
    [],
  );

  const tool = useMemo(
    () => ({
      toolName: 'find_and_open_contact',
      type: 'frontend',
      description:
        'Find a contact by name or email and open it when there is one clear match. Use this when the user asks to find, show, or open a specific contact record. If multiple contacts match, return them and ask the user to choose instead of guessing.',
      parameters,
      execute: async ({ query }) => {
        const keyword = query?.trim?.();
        if (!keyword || keyword.length < 2) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'Contact search query must be at least 2 characters.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_and_open_contact',
            toolResult: result,
          }));
          return result;
        }
        if (!hasPermission(P.VIEW_CONTACTS)) {
          const result = restrictedResult('contacts');
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_and_open_contact',
            toolResult: result,
          }));
          return result;
        }

        const merchants = await getMerchantNames(organizationName);
        if (merchants.length === 0) {
          const result = {
            runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
            status: 'error',
            message: 'No active organization name is available for contact search.',
          };
          dispatchActionMessage(createFrontendToolErrorMessage({
            message: result.message,
            status: result.status,
            toolArgs: { query: keyword },
            toolName: 'find_and_open_contact',
            toolResult: result,
          }));
          return result;
        }

        const contactsRaw = await searchContacts(merchants, keyword);
        const contacts = limitItems(
          dedupeBy(contactsRaw, (contact) => contact.contactID),
          MAX_SEARCH_RESULTS_PER_CATEGORY,
        );
        const searchResult = buildSearchResult({ contacts, organizationName, query: keyword });
        const displayPayload = {
          renderer: 'dashboard_search_results',
          summary: `Searched contacts for "${keyword}"`,
          data: searchResult,
        };

        const compactResult = createCompactSearchToolResult(searchResult);
        dispatchActionMessage(createFrontendToolActionMessage({
          actionType: 'search',
          content: displayPayload.summary,
          displayPayload,
          status: compactResult.status,
          toolArgs: { query: keyword },
          toolName: 'find_and_open_contact',
          toolResult: compactResult,
        }));

        const rankedContacts = contacts
          .map((contact) => ({ contact, score: getContactMatchScore(contact, keyword) }))
          .filter(({ score }) => score > 0)
          .sort((left, right) => right.score - left.score);
        const clearMatch =
          rankedContacts.length === 1 ||
          (rankedContacts.length > 1 && rankedContacts[0].score >= 95 && rankedContacts[1].score < 95)
            ? rankedContacts[0].contact
            : contacts.length === 1
              ? contacts[0]
              : null;

        if (!clearMatch?.contactID) {
          return {
            ...compactResult,
            status: contacts.length > 0 ? 'needs_disambiguation' : 'not_found',
            guidance:
              contacts.length > 0
                ? 'Ask the user which contact to open. Do not open a record unless there is one clear match.'
                : 'Tell the user no matching contact was found in the active organization.',
          };
        }

        const path = `/contacts?contact=${encodeURIComponent(clearMatch.contactID)}&noSheet=1`;
        history.push(path);
        dispatchActionMessage(createFrontendNavigationToolMessage({
          label: `contact: ${clearMatch.name || clearMatch.email || clearMatch.contactID}`,
          path,
          toolArgs: { query: keyword },
          toolName: 'find_and_open_contact',
          toolResult: {
            contact: serializeSearchContact(clearMatch),
            path,
            status: 'opened',
          },
        }));

        return {
          runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
          status: 'opened',
          path,
          contact: serializeSearchContact(clearMatch),
        };
      },
    }),
    [activeSessionId, dispatchActionMessage, history, organizationName, parameters],
  );

  useAssistantTool(tool);
}
